const SCROLL_CLOSE_DEBOUNCE_MS = 100;

/**
 * @param {Function} fn
 * @param {number} delay
 * @returns {(...args: any[]) => void}
 */
function debounceScrollClose(fn, delay) {
  let timeoutId;
  return function debounced(...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      fn.apply(this, args);
    }, delay);
  };
}

/** @param {HTMLElement} menu */
function hideCartMenuWhenOverlappingTitle(menu) {
  const cartTitle = document.querySelector('.cart-page__title');
  if (!(cartTitle instanceof HTMLElement) || getComputedStyle(cartTitle).display === 'none') {
    menu.style.visibility = '';
    return;
  }

  const menuRect = menu.getBoundingClientRect();
  const titleRect = cartTitle.getBoundingClientRect();
  const overlapsTitle =
    menuRect.left < titleRect.right &&
    menuRect.right > titleRect.left &&
    menuRect.top < titleRect.bottom &&
    menuRect.bottom > titleRect.top;
  menu.style.visibility = overlapsTitle ? 'hidden' : '';
}

/**
 * Accessible menubutton + `role="menu"` for `.rs-cart-multiple__select` (cart + warranty).
 * Wires: Enter/Space/ArrowDown from trigger, arrow cycling, Tab/Shift+Tab exit,
 * Esc closes to trigger, is-open for chevron, document outside-click close.
 * @param {HTMLButtonElement} combobox
 * @param {HTMLElement} menu
 * @param {(itemEl: HTMLButtonElement) => void} onOptionActivate
 */
function wireRsCartMultipleCombobox(combobox, menu, onOptionActivate) {
  if (!(combobox instanceof HTMLButtonElement) || !menu) return;

  if (combobox.dataset.rsCartComboboxWired === '1') return;
  combobox.dataset.rsCartComboboxWired = '1';

  const selectRoot = combobox.closest('.rs-cart-multiple__select') || combobox.parentElement;

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /** @returns {HTMLButtonElement[]} */
  function getOptions() {
    return /** @type {HTMLButtonElement[]} */ (Array.from(menu.querySelectorAll('.rs-cart-multiple__option[role="menuitem"]')));
  }

  function isOpen() {
    return !menu.classList.contains('rs-hidden');
  }

  /** @param {number} activeIndex  */
  function setRovingIndex(activeIndex) {
    const opts = getOptions();
    const n = opts.length;
    if (n === 0) return;
    const i = ((activeIndex % n) + n) % n;
    opts.forEach((o, j) => {
      o.setAttribute('tabindex', j === i ? '0' : '-1');
    });
    opts[i].focus({preventScroll: true});
  }

  /**
   * @param {boolean} focusCombobox
   * @param {{ force?: boolean, skipFocus?: boolean }} [closeExtra]
   */
  function close(focusCombobox, closeExtra) {
    const force = Boolean(closeExtra && closeExtra.force);
    if (!isOpen() && !force) {
      if (focusCombobox) {
        if (!closeExtra?.skipFocus) combobox.focus({preventScroll: true});
      }
      return;
    }
    menu.classList.add('rs-hidden');
    menu.style.visibility = '';
    combobox.setAttribute('aria-expanded', 'false');
    combobox.classList.remove('is-open');
    getOptions().forEach((o) => o.setAttribute('tabindex', '-1'));
    if (focusCombobox && !closeExtra?.skipFocus) {
      combobox.focus({preventScroll: true});
    }
  }

  function open(/** @type {number | 'active'} */ start) {
    const opts = getOptions();
    if (opts.length === 0) return;
    menu.style.visibility = '';
    menu.classList.remove('rs-hidden');
    combobox.setAttribute('aria-expanded', 'true');
    combobox.classList.add('is-open');
    let idx = 0;
    if (start === 'active') {
      const a = menu.querySelector('.rs-cart-multiple__option.rs-active');
      const ai = a != null ? opts.indexOf(/** @type {HTMLButtonElement} */ (a)) : 0;
      idx = ai >= 0 ? ai : 0;
    } else if (typeof start === 'number' && !Number.isNaN(start)) {
      idx = start;
    }
    setRovingIndex(idx);
  }

  /** @returns {HTMLElement | null} */
  function getNextFocusableAfterCombobox() {
    const all = getVisibleFocusableDocument();
    const cIdx = all.indexOf(combobox);
    if (cIdx < 0) return null;
    for (let j = cIdx + 1; j < all.length; j += 1) {
      if (!menu.contains(all[j])) return all[j];
    }
    return null;
  }

  /** @returns {HTMLElement | null} */
  function getPreviousFocusableBeforeCombobox() {
    const all = getVisibleFocusableDocument();
    const cIdx = all.indexOf(combobox);
    if (cIdx < 0) return null;
    for (let j = cIdx - 1; j >= 0; j -= 1) {
      if (!menu.contains(all[j])) return all[j];
    }
    return null;
  }

  /** @returns {HTMLElement[]} */
  function getVisibleFocusableDocument() {
    return Array.from(document.querySelectorAll(FOCUSABLE)).filter((el) => {
      if (!(el instanceof HTMLElement) || el.hasAttribute('hidden')) return false;
      if (el.tabIndex < 0) return false;
      if (el.closest('[inert]')) return false;
      if (el.getClientRects().length === 0) return false;
      return true;
    });
  }

  function onDocumentPointerDown(/** @type {MouseEvent} */ e) {
    if (!isOpen()) return;
    const t = e.target;
    if (t instanceof Node && selectRoot && selectRoot.contains(t)) return;
    close(false, {force: true, skipFocus: true});
  }

  document.addEventListener('mousedown', onDocumentPointerDown, true);

  const closeAfterScroll = debounceScrollClose(() => {
    if (!isOpen()) return;
    close(false, {force: true, skipFocus: true});
  }, SCROLL_CLOSE_DEBOUNCE_MS);

  document.addEventListener(
    'scroll',
    () => {
      if (!isOpen()) return;
      hideCartMenuWhenOverlappingTitle(menu);
      closeAfterScroll();
    },
    {capture: true, passive: true},
  );

  function toggle() {
    if (isOpen()) {
      close(true, {force: true});
    } else {
      open('active');
    }
  }

  combobox.addEventListener('click', (e) => {
    e.stopPropagation();
    toggle();
  });

  combobox.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown' && !e.altKey) {
      e.preventDefault();
      if (!isOpen()) {
        open(0);
      } else {
        setRovingIndex(0);
      }
      return;
    }
    if (e.key === 'ArrowUp' && !e.altKey) {
      e.preventDefault();
      if (!isOpen()) {
        const o = getOptions();
        if (o.length) open(o.length - 1);
      } else {
        const o = getOptions();
        if (o.length) setRovingIndex(o.length - 1);
      }
      return;
    }
    if (e.key === 'Escape' && isOpen()) {
      e.preventDefault();
      close(true, {force: true});
    }
  });

  menu.addEventListener('keydown', (e) => {
    const opts = getOptions();
    const n = opts.length;
    if (n === 0) return;
    const active = document.activeElement;
    const fromIdx = active instanceof HTMLButtonElement ? opts.indexOf(active) : 0;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen()) return;
      setRovingIndex((fromIdx >= 0 ? fromIdx : 0) + 1);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!isOpen()) return;
      setRovingIndex((fromIdx >= 0 ? fromIdx : 0) - 1);
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      close(true, {force: true});
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      if (e.shiftKey) {
        const p = getPreviousFocusableBeforeCombobox();
        close(false, {force: true, skipFocus: true});
        if (p) p.focus({preventScroll: true});
      } else {
        const nx = getNextFocusableAfterCombobox();
        close(false, {force: true, skipFocus: true});
        if (nx) nx.focus({preventScroll: true});
      }
    }
  });

  menu.addEventListener('click', (e) => {
    const t = e.target;
    if (!(t instanceof Node)) return;
    const opt = t.closest && t.closest('.rs-cart-multiple__option[role="menuitem"]');
    if (!opt || !menu.contains(/** @type {Node} */ (opt)) || !(opt instanceof HTMLButtonElement)) return;
    e.preventDefault();
    onOptionActivate(opt);
    close(true, {force: true});
  });

  getOptions().forEach((o) => o.setAttribute('tabindex', '-1'));
  close(false, {force: true, skipFocus: true});
  combobox.setAttribute('aria-expanded', 'false');
  combobox.classList.remove('is-open');
}

const MORE_ACTIONS_MENU_BUTTON_LABEL = 'More actions, menu';
const MORE_ACTIONS_MENU_BUTTON_LABEL_EXPANDED = 'More actions, menu, expanded';

/**
 * @param {HTMLButtonElement} combobox
 * @param {boolean} expanded
 * @returns {string}
 */
function getMoreActionsMenuAriaLabel(combobox, expanded) {
  const fromData = expanded ? combobox.dataset.ariaLabelExpanded : combobox.dataset.ariaLabelCollapsed;
  if (fromData && String(fromData).trim()) {
    return String(fromData).trim();
  }

  const __t = /** @type {any} */ (window).__t;
  if (typeof __t === 'function') {
    const key = expanded ? 'more_actions_menu_button_expanded' : 'more_actions_menu_button';
    const translated = __t(key);
    if (translated && translated !== key) {
      return translated;
    }
  }

  return expanded ? MORE_ACTIONS_MENU_BUTTON_LABEL_EXPANDED : MORE_ACTIONS_MENU_BUTTON_LABEL;
}

/**
 * @param {HTMLButtonElement} combobox
 * @param {boolean} expanded
 */
function applyMoreActionsMenuAccessibleName(combobox, expanded) {
  combobox.setAttribute('aria-label', getMoreActionsMenuAriaLabel(combobox, expanded));
  combobox.removeAttribute('aria-expanded');
  combobox.removeAttribute('aria-labelledby');
}

const CART_PAGINATION_ROWS_STATE_COLLAPSED = 'collapsed';
const CART_PAGINATION_ROWS_STATE_OPEN = 'open';
const CART_PAGINATION_ROWS_PER_PAGE_LABEL = 'Rows per page';

/**
 * @param {HTMLButtonElement} combobox
 * @returns {string}
 */
function getCartPaginationRowsPerPageLabel(combobox) {
  const fromData = combobox.dataset.ariaLabelRowsPerPage;
  if (fromData && String(fromData).trim()) {
    return String(fromData).trim();
  }

  const __t = /** @type {any} */ (window).__t;
  if (typeof __t === 'function') {
    const translated = __t('rows_per_page');
    if (translated && translated !== 'rows_per_page') {
      return translated;
    }
  }

  return CART_PAGINATION_ROWS_PER_PAGE_LABEL;
}

/**
 * @param {HTMLButtonElement} combobox
 * @returns {string}
 */
function getCartPaginationRowsCount(combobox) {
  const selectEl = combobox.closest('.rs-cart-pagination__rows-select');
  const active = selectEl?.querySelector('.rs-cart-pagination__rows-option.rs-active');
  const fromActive = active?.getAttribute('value');
  if (fromActive && String(fromActive).trim()) {
    return String(fromActive).trim();
  }
  return '10';
}

/**
 * @param {HTMLButtonElement} combobox
 * @param {boolean} expanded
 * @returns {string}
 */
function getCartPaginationRowsStateLabel(combobox, expanded) {
  const fromData = expanded ? combobox.dataset.ariaLabelExpanded : combobox.dataset.ariaLabelCollapsed;
  if (fromData && String(fromData).trim()) {
    return String(fromData).trim();
  }

  const __t = /** @type {any} */ (window).__t;
  if (typeof __t === 'function') {
    const key = expanded ? 'rows_select_open' : 'rows_select_collapsed';
    const translated = __t(key);
    if (translated && translated !== key) {
      return translated;
    }
  }

  return expanded ? CART_PAGINATION_ROWS_STATE_OPEN : CART_PAGINATION_ROWS_STATE_COLLAPSED;
}

/**
 * @param {HTMLButtonElement} combobox
 * @param {boolean} expanded
 */
function applyCartPaginationRowsAccessibleName(combobox, expanded) {
  const rowsPerPageLabel = getCartPaginationRowsPerPageLabel(combobox);
  const count = getCartPaginationRowsCount(combobox);
  const stateLabel = getCartPaginationRowsStateLabel(combobox, expanded);
  combobox.setAttribute('aria-label', `${rowsPerPageLabel}, ${count}, ${stateLabel}`);
  combobox.removeAttribute('aria-expanded');
  combobox.removeAttribute('aria-haspopup');
}

const RS_ACTIONS_MENU_ITEM_SELECTOR = '[data-more-actions-key]';

/**
 * @param {HTMLElement} menu
 * @returns {HTMLElement[]}
 */
function getMoreActionsMenuItems(menu) {
  return Array.from(menu.querySelectorAll(RS_ACTIONS_MENU_ITEM_SELECTOR)).filter((el) => {
    if (!(el instanceof HTMLElement)) return false;
    if (!el.matches('a[href], button:not([disabled])')) return false;
    return true;
  });
}

/**
 * Wrap actions in presentational list items so VoiceOver does not treat the panel as a "group".
 * @param {HTMLElement} menu
 */
function structureMoreActionsMenuPanel(menu) {
  if (!(menu instanceof HTMLElement)) return;
  if (menu.querySelector(':scope > .rs-actions__list')) return;

  const items = getMoreActionsMenuItems(menu);
  const list = document.createElement('ul');
  list.className = 'rs-actions__list';
  list.setAttribute('role', 'presentation');

  items.forEach((el) => {
    const li = document.createElement('li');
    li.setAttribute('role', 'presentation');
    li.appendChild(el);
    list.appendChild(li);
  });

  menu.setAttribute('role', 'presentation');
  menu.appendChild(list);
}

/**
 * Full "option, 1 of 4" on aria-label only (no labelledby children — VO says "group").
 * @param {HTMLElement} menu
 */
function syncMoreActionsMenuItemLabels(menu) {
  if (!(menu instanceof HTMLElement)) return;
  structureMoreActionsMenuPanel(menu);

  const __t = /** @type {(key: string) => string} */ ((/** @type {any} */ (window)).__t || ((k) => k));
  const items = getMoreActionsMenuItems(menu);

  items.forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    el.removeAttribute('role');
    el.removeAttribute('aria-labelledby');
    el.querySelector('.rs-actions-menuitem-label')?.remove();

    const labelKey = el.getAttribute('data-more-actions-key');
    const labelText = labelKey ? __t(labelKey) : '';
    if (!labelText || labelText === labelKey) return;

    el.setAttribute('aria-label', labelText);

    el.querySelectorAll('svg').forEach((svg) => {
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
    });
    el.querySelectorAll('.rs-button > span').forEach((span) => {
      if (span instanceof HTMLElement) span.setAttribute('aria-hidden', 'true');
    });
  });
}

/**
 * Menubutton pattern for one `.rs-actions` (delegates open/close to `RsActions`).
 * @param {HTMLElement} root
 * @param {{ menuIdBase: string, isTableRow?: boolean }} opts
 */
function wireApgForOneRsActionsRoot(/** @type {HTMLElement} */ root, /** @type {{ menuIdBase: string, isTableRow?: boolean }} */ opts) {
  if (!(root instanceof HTMLElement)) return;
  if (root.getAttribute('data-rsa-apg-wired') === '1') return;
  if (opts.isTableRow && root.getAttribute('data-rsa-table-row-apg-wired') === '1') return;
  const combobox = root.querySelector('.rs-actions__toggle');
  const menu = root.querySelector('.rs-actions__content');
  if (!(combobox instanceof HTMLButtonElement) || !(menu instanceof HTMLElement)) return;
  /** @type {Record<string, unknown>} */ (root)._rsActionsMenuEl = menu;

  if (combobox.dataset.rsCartApgWired === '1') return;
  combobox.dataset.rsCartApgWired = '1';
  root.setAttribute('data-rsa-apg-wired', '1');
  if (opts.isTableRow) {
    root.setAttribute('data-rsa-table-row-apg-wired', '1');
  }

  if (window.RsActions && typeof window.RsActions.init === 'function') {
    window.RsActions.init(root);
  }

  if (!menu.id) {
    const base = opts.menuIdBase;
    if (!opts.isTableRow && !document.getElementById(base)) {
      menu.id = base;
    } else {
      menu.id = `${base}-${Math.random().toString(36).slice(2, 9)}`;
    }
  }

  combobox.removeAttribute('aria-haspopup');
  combobox.removeAttribute('aria-controls');

  combobox.querySelectorAll('svg').forEach((svg) => {
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
  });
  applyMoreActionsMenuAccessibleName(combobox, false);
  syncMoreActionsMenuItemLabels(menu);

  const selectRoot = root.closest('.rs-actions-host') || root;

  function allMenuitemElements() {
    return getMoreActionsMenuItems(menu);
  }

  function getOptions() {
    return allMenuitemElements().filter((el) => el.getClientRects().length > 0);
  }

  function isOpen() {
    return !menu.hidden;
  }

  function setMenuitemsTabbable(/** @type {boolean} */ enabled) {
    allMenuitemElements().forEach((o) => o.setAttribute('tabindex', enabled ? '0' : '-1'));
  }

  function focusMenuitemByIndex(activeIndex) {
    const opts = getOptions();
    const n = opts.length;
    if (n === 0) return;
    const i = ((activeIndex % n) + n) % n;
    /** @type {HTMLElement} */ (opts[i]).focus({preventScroll: true});
  }

  function close(/** @type {boolean} */ focusCombobox, closeExtra) {
    const force = Boolean(closeExtra && closeExtra.force);
    if (!isOpen() && !force) {
      if (focusCombobox && !closeExtra?.skipFocus) combobox.focus({preventScroll: true});
      return;
    }
    setMenuitemsTabbable(false);
    applyMoreActionsMenuAccessibleName(combobox, false);
    menu.setAttribute('aria-hidden', 'true');
    menu.setAttribute('inert', '');
    combobox.classList.remove('is-open');
    if (window.RsActions && typeof window.RsActions.close === 'function') {
      window.RsActions.close(root);
    } else {
      menu.hidden = true;
      root.classList.remove('rs-active');
      if (typeof window.RsActions?.detachMenuPanel === 'function') {
        window.RsActions.detachMenuPanel(root);
      }
    }
    if (focusCombobox && !closeExtra?.skipFocus) {
      combobox.focus({preventScroll: true});
    }
  }

  function open(/** @type {number | 'active' | 0} */ start) {
    if (window.RsActions && typeof window.RsActions.open === 'function') {
      window.RsActions.open(root);
    } else {
      menu.hidden = false;
      root.classList.add('rs-active');
    }
    applyMoreActionsMenuAccessibleName(combobox, true);
    menu.removeAttribute('aria-hidden');
    menu.removeAttribute('inert');
    combobox.classList.add('is-open');
    syncMoreActionsMenuItemLabels(menu);
    setMenuitemsTabbable(true);
    const opts = getOptions();
    if (opts.length === 0) return;
    let idx = 0;
    if (start === 'active' || start === 0) {
      idx = 0;
    } else if (typeof start === 'number' && !Number.isNaN(/** @type {number} */ (start))) {
      idx = /** @type {number} */ (start);
    }
    focusMenuitemByIndex(idx);
  }

  function onDocumentPointerDown(/** @type {MouseEvent} */ e) {
    if (!isOpen()) return;
    const t = e.target;
    if (t instanceof Node && selectRoot.contains(t)) return;
    // Keep menu open while a modal opened from this menu is being interacted with
    if (t instanceof Element && t.closest('delivery-dates-modal')) return;
    close(false, {force: true, skipFocus: true});
  }

  document.addEventListener('mousedown', onDocumentPointerDown, true);

  const closeAfterScroll = debounceScrollClose(() => {
    if (!isOpen()) return;
    close(false, {force: true, skipFocus: true});
  }, SCROLL_CLOSE_DEBOUNCE_MS);

  document.addEventListener(
    'scroll',
    () => {
      if (!isOpen()) return;
      if (typeof window.RsActions?.positionMenu === 'function') window.RsActions.positionMenu(root);
      closeAfterScroll();
    },
    {capture: true, passive: true},
  );

  function toggle() {
    if (isOpen()) {
      close(true, {force: true});
    } else {
      open(/** @type {'active'} */ ('active'));
    }
  }

  combobox.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggle();
  });

  combobox.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      toggle();
      return;
    }
    if (e.key === 'ArrowDown' && !e.altKey) {
      e.preventDefault();
      e.stopPropagation();
      if (!isOpen() && allMenuitemElements().length) open(0);
      else if (isOpen()) focusMenuitemByIndex(0);
      return;
    }
    if (e.key === 'ArrowUp' && !e.altKey) {
      e.preventDefault();
      e.stopPropagation();
      const o = allMenuitemElements();
      if (!isOpen() && o.length) open(o.length - 1);
      else {
        const vis = getOptions();
        if (vis.length) focusMenuitemByIndex(vis.length - 1);
      }
      return;
    }
    if (e.key === 'Escape' && isOpen()) {
      e.preventDefault();
      e.stopPropagation();
      close(true, {force: true});
    }
  });

  menu.addEventListener('keydown', (e) => {
    const opts = getOptions();
    if (opts.length === 0) return;
    const active = document.activeElement;
    const ai = active ? opts.indexOf(/** @type {any} */ (active)) : -1;
    const fromIdx = ai >= 0 ? ai : 0;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();
      if (!isOpen()) return;
      focusMenuitemByIndex(fromIdx + 1);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      if (!isOpen()) return;
      focusMenuitemByIndex(fromIdx - 1);
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close(true, {force: true});
      return;
    }
  });

  // Let Tab/Shift+Tab follow the visible menu hierarchy; close only when focus leaves this host.
  selectRoot.addEventListener('focusout', (e) => {
    if (!isOpen()) return;
    const next = /** @type {FocusEvent} */ (e).relatedTarget;
    if (!(next instanceof Node)) {
      close(false, {force: true, skipFocus: true});
      return;
    }
    if (selectRoot.contains(next)) return;
    // Keep menu open when focus moves into a modal triggered from this menu
    if (next instanceof Element && next.closest('delivery-dates-modal')) return;
    close(false, {force: true, skipFocus: true});
  });

  menu.addEventListener('mousedown', (e) => {
    if (!(e instanceof MouseEvent) || e.button !== 0) return;
    if (!(e.target instanceof Element)) return;
    const it = e.target.closest(RS_ACTIONS_MENU_ITEM_SELECTOR);
    if (!it || !menu.contains(it) || !(it instanceof HTMLElement)) return;
    if (!it.classList.contains('packing-list-notes-trigger')) return;

    // Open on mousedown so focusout cannot detach the panel before click fires.
    // preventDefault avoids click-through onto the row after the menu closes.
    e.preventDefault();
    e.stopPropagation();
    const row = it.closest('.rs-table-body__row');
    if (
      row instanceof HTMLElement &&
      !row.querySelector('.cart-items__item-details--notes') &&
      window.PackingListNotes &&
      typeof window.PackingListNotes.showPackingListNotes === 'function'
    ) {
      window.PackingListNotes.showPackingListNotes(it, row);
    }
    close(true, {force: true, skipFocus: true});
  });

  menu.addEventListener('click', (e) => {
    if (!(e.target instanceof Node) || e.target == null) return;
    const it = e.target.closest && e.target.closest(RS_ACTIONS_MENU_ITEM_SELECTOR);
    if (!it || !menu.contains(it) || !(it instanceof HTMLElement)) return;
    e.stopPropagation();

    if (it instanceof HTMLButtonElement && it.dataset.actionUrl) {
      e.preventDefault();
      const actionUrl = it.dataset.actionUrl;

      if (opts.isTableRow) {
        const row = it.closest('.rs-table-body__row');
        const cartTable = document.querySelector('cart-table');
        if (
          row instanceof HTMLElement &&
          cartTable &&
          typeof /** @type {any} */ (cartTable).handleCartRowRemove === 'function' &&
          (row.getAttribute('data-multiple-delivery') === 'true' || !row.hasAttribute('data-delivery-date'))
        ) {
          void /** @type {any} */ (cartTable).handleCartRowRemove(row, actionUrl);
          close(true, {force: true});
          return;
        }
      }

      if (typeof window.notifyCartLineRemovedBeforeNavigate === 'function') {
        window.notifyCartLineRemovedBeforeNavigate(actionUrl);
      }
      window.location.assign(actionUrl);
      return;
    }

    // Packing list notes: open on the row before the panel is detached (clone rows included).
    // Prefer mousedown handler above; this covers keyboard activation.
    if (it.classList.contains('packing-list-notes-trigger')) {
      e.preventDefault();
      const row = it.closest('.rs-table-body__row');
      if (
        row instanceof HTMLElement &&
        !row.querySelector('.cart-items__item-details--notes') &&
        window.PackingListNotes &&
        typeof window.PackingListNotes.showPackingListNotes === 'function'
      ) {
        window.PackingListNotes.showPackingListNotes(it, row);
      }
      close(true, {force: true});
      return;
    }

    close(true, {force: true});
  });

  setMenuitemsTabbable(false);
  close(false, {force: true, skipFocus: true});
  applyMoreActionsMenuAccessibleName(combobox, false);
  combobox.classList.remove('is-open');
}

/**
 * More actions in the cart page actions bar
 */
function wireCartPageActionsRsMenu() {
  document.querySelectorAll('.cart-page-actions__actions .rs-actions').forEach((root) => {
    if (!(root instanceof HTMLElement)) return;
    wireApgForOneRsActionsRoot(root, {menuIdBase: 'cart-page-actions-rs-menu', isTableRow: false});
  });
}

/**
 * Row action menus in the cart table: same menubutton APG as the row-count + page bar `rs-actions`.
 * @param {Element | Document} [searchRoot]  defaults to `document` (wires all cart-table action cells)
 */
function wireCartTableRowActionsMenu(/** @type {Element|Document|undefined} */ searchRoot) {
  const r = searchRoot && 'querySelectorAll' in searchRoot ? searchRoot : document;
  const isDoc = r === document || (r && r.nodeType === 9);
  const nodes = isDoc
    ? document.querySelectorAll('cart-table .rs-table-body__cell--actions .rs-actions')
    : /** @type {ParentNode} */ (r).querySelectorAll('.rs-table-body__cell--actions .rs-actions');
  nodes.forEach((node) => {
    if (node instanceof HTMLElement) {
      wireApgForOneRsActionsRoot(node, {menuIdBase: 'rs-cart-table-row-rs-menu', isTableRow: true});
    }
  });
}

/**
 * `role="listbox"` for cart pagination. Wires every `.rs-cart-pagination__rows-button` (and its
 * sibling listbox) with the same APG as other cart menus: Enter/Space, arrows, roving options,
 * Esc, outside mousedown, `.is-open` on the wrapper.
 * @param {Element | Document} [searchRoot] defaults to `document`
 */
function wireCartPaginationRowsSelect(/** @type {Element|Document|undefined} */ searchRoot) {
  const root = searchRoot && 'querySelectorAll' in searchRoot ? searchRoot : document;
  const listRoot = /** @type {ParentNode} */ (root);
  listRoot.querySelectorAll('.rs-cart-pagination__rows-button').forEach((combobox) => {
    if (!(combobox instanceof HTMLButtonElement)) return;
    if (combobox.dataset.rsCartPagrowsApgWired === '1') return;
    const selectEl = combobox.closest('.rs-cart-pagination__rows-select');
    if (!(selectEl instanceof HTMLElement)) return;
    const menu = selectEl.querySelector('.rs-cart-pagination__rows-options');
    if (!(menu instanceof HTMLElement)) return;

    combobox.dataset.rsCartPagrowsApgWired = '1';
    selectEl.setAttribute('data-pagrows-apg-wired', '1');

    if (!menu.id) {
      const base = 'rs-cart-rows-listbox';
      if (!document.getElementById(base)) {
        menu.id = base;
      } else {
        menu.id = `${base}-${Math.random().toString(36).slice(2, 7)}`;
      }
    }
    if (!combobox.getAttribute('aria-controls')) {
      combobox.setAttribute('aria-controls', menu.id);
    }

    const FOCUS = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    /** @returns {HTMLButtonElement[]} */
    function allOptions() {
      return /** @type {HTMLButtonElement[]} */ (
        Array.from(menu.querySelectorAll('.rs-cart-pagination__rows-option[role="option"]'))
      );
    }

    /** @returns {HTMLButtonElement[]} */
    function getOptions() {
      return allOptions().filter((el) => el.getClientRects().length > 0);
    }

    function isOpen() {
      return selectEl.classList.contains('is-open');
    }

    function setRovingIndex(/** @type {number} */ activeIndex) {
      const opts = getOptions();
      const n = opts.length;
      if (n === 0) return;
      const i = ((activeIndex % n) + n) % n;
      opts.forEach((o, j) => {
        o.setAttribute('tabindex', j === i ? '0' : '-1');
      });
      opts[i].focus({preventScroll: true});
    }

    function close(/** @type {boolean} */ focusCombobox, closeExtra) {
      const force = Boolean(closeExtra && closeExtra.force);
      if (!isOpen() && !force) {
        if (focusCombobox && !closeExtra?.skipFocus) combobox.focus({preventScroll: true});
        return;
      }
      allOptions().forEach((o) => o.setAttribute('tabindex', '-1'));
      applyCartPaginationRowsAccessibleName(combobox, false);
      combobox.classList.remove('is-open');
      selectEl.classList.remove('is-open');
      if (focusCombobox && !closeExtra?.skipFocus) {
        combobox.focus({preventScroll: true});
      }
    }

    function open(/** @type {number | 'active' | 0} */ start) {
      selectEl.classList.add('is-open');
      applyCartPaginationRowsAccessibleName(combobox, true);
      combobox.classList.add('is-open');
      const opts = getOptions();
      if (opts.length === 0) return;
      let idx = 0;
      if (start === 'active') {
        const a = menu.querySelector('.rs-cart-pagination__rows-option.rs-active');
        const ai = a != null ? opts.indexOf(/** @type {HTMLButtonElement} */ (a)) : 0;
        idx = ai >= 0 ? ai : 0;
      } else if (start === 0) {
        idx = 0;
      } else if (typeof start === 'number' && !Number.isNaN(/** @type {number} */ (start))) {
        idx = /** @type {number} */ (start);
      }
      setRovingIndex(idx);
    }

    function getVisibleFocusableDocument() {
      return Array.from(document.querySelectorAll(FOCUS)).filter((el) => {
        if (!(el instanceof HTMLElement) || el.hasAttribute('hidden')) return false;
        if (el.tabIndex < 0) return false;
        if (el.closest('[inert]')) return false;
        if (el.getClientRects().length === 0) return false;
        return true;
      });
    }

    function getNextFocusableAfterCombobox() {
      const all = getVisibleFocusableDocument();
      const cIdx = all.indexOf(combobox);
      if (cIdx < 0) return null;
      for (let j = cIdx + 1; j < all.length; j += 1) {
        if (!menu.contains(all[j])) return /** @type {HTMLElement} */ (all[j]);
      }
      return null;
    }

    function getPreviousFocusableBeforeCombobox() {
      const all = getVisibleFocusableDocument();
      const cIdx = all.indexOf(combobox);
      if (cIdx < 0) return null;
      for (let j = cIdx - 1; j >= 0; j -= 1) {
        if (!menu.contains(all[j])) return /** @type {HTMLElement} */ (all[j]);
      }
      return null;
    }

    function onDocumentPointerDown(/** @type {MouseEvent} */ e) {
      if (!isOpen()) return;
      const t = e.target;
      if (t instanceof Node && selectEl.contains(t)) return;
      close(false, {force: true, skipFocus: true});
    }

    document.addEventListener('mousedown', onDocumentPointerDown, true);

    function toggle() {
      if (isOpen()) {
        close(true, {force: true});
      } else {
        open(/** @type {'active'} */ ('active'));
      }
    }

    combobox.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      toggle();
    });

    combobox.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        toggle();
        return;
      }
      if (e.key === 'ArrowDown' && !e.altKey) {
        e.preventDefault();
        e.stopPropagation();
        if (!isOpen() && allOptions().length) open(0);
        else if (isOpen()) setRovingIndex(0);
        return;
      }
      if (e.key === 'ArrowUp' && !e.altKey) {
        e.preventDefault();
        e.stopPropagation();
        const n = allOptions().length;
        if (!isOpen() && n) open(n - 1);
        else if (isOpen()) {
          const vis = getOptions();
          if (vis.length) setRovingIndex(vis.length - 1);
        }
        return;
      }
      if (e.key === 'Escape' && isOpen()) {
        e.preventDefault();
        e.stopPropagation();
        close(true, {force: true});
      }
    });

    menu.addEventListener('keydown', (e) => {
      const opts = getOptions();
      if (opts.length === 0) return;
      const active = document.activeElement;
      const ai = active && opts.indexOf(/** @type {any} */ (active)) >= 0
        ? opts.indexOf(/** @type {any} */ (active))
        : -1;
      const fromIdx = ai >= 0 ? ai : 0;
      if (e.key === 'Home') {
        e.preventDefault();
        e.stopPropagation();
        if (!isOpen()) return;
        setRovingIndex(0);
        return;
      }
      if (e.key === 'End') {
        e.preventDefault();
        e.stopPropagation();
        if (!isOpen()) return;
        setRovingIndex(opts.length - 1);
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        if (!isOpen()) return;
        setRovingIndex(fromIdx + 1);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        if (!isOpen()) return;
        setRovingIndex(fromIdx - 1);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        close(true, {force: true});
        return;
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        e.stopPropagation();
        if (e.shiftKey) {
          const pEl = getPreviousFocusableBeforeCombobox();
          close(false, {force: true, skipFocus: true});
          if (pEl) pEl.focus({preventScroll: true});
        } else {
          const nx = getNextFocusableAfterCombobox();
          close(false, {force: true, skipFocus: true});
          if (nx) nx.focus({preventScroll: true});
        }
      }
    });

    menu.addEventListener('click', (e) => {
      if (!(e.target instanceof Node) || e.target == null) return;
      const it = e.target.closest && e.target.closest('[role="option"]');
      if (!it || !menu.contains(/** @type {Node} */ (it)) || !(it instanceof HTMLElement)) return;
      e.stopPropagation();
      close(true, {force: true});
    });

    allOptions().forEach((o) => o.setAttribute('tabindex', '-1'));
    close(false, {force: true, skipFocus: true});
    applyCartPaginationRowsAccessibleName(combobox, false);
    combobox.classList.remove('is-open');
    selectEl.classList.remove('is-open');
  });
}

function wireAllCartRsActionsMenus() {
  wireCartPageActionsRsMenu();
  const cartTable = document.querySelector('cart-table');
  wireCartTableRowActionsMenu(cartTable || document);
}

if (typeof window !== 'undefined') {
  window.wireRsCartMultipleCombobox = wireRsCartMultipleCombobox;
  window.wireCartPageActionsRsMenu = wireCartPageActionsRsMenu;
  window.wireCartTableRowActionsMenu = wireCartTableRowActionsMenu;
  window.wireCartPaginationRowsSelect = wireCartPaginationRowsSelect;
  window.wireAllCartRsActionsMenus = wireAllCartRsActionsMenus;
  window.getMoreActionsMenuAriaLabel = getMoreActionsMenuAriaLabel;
  window.applyMoreActionsMenuAccessibleName = applyMoreActionsMenuAccessibleName;
  window.applyCartPaginationRowsAccessibleName = applyCartPaginationRowsAccessibleName;
  window.syncMoreActionsMenuItemLabels = syncMoreActionsMenuItemLabels;
  window.structureMoreActionsMenuPanel = structureMoreActionsMenuPanel;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wireAllCartRsActionsMenus);
  } else {
    wireAllCartRsActionsMenus();
  }
}
